import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Warning,
  Error as ErrorIcon,
  HelpOutline,
} from '@mui/icons-material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import {
  WorkersV2API,
  ConsolidatedSkill,
  ConsolidatedSkillsResponse,
} from '@/services/workersV2API';
import AddSkillModal from './AddSkillModal';

type Props = {
  workerId: number;
};

type SectionConfig = {
  key: 'approved' | 'needs_review' | 'in_progress' | 'ineligible';
  label: string;
  borderColor: string;
  bgColor: string;
};

const SECTIONS: SectionConfig[] = [
  {
    key: 'approved',
    label: 'Approved',
    borderColor: '#A7F3D0',
    bgColor: '#ECFDF5',
  },
  {
    key: 'needs_review',
    label: 'Needs Review',
    borderColor: '#FDE68A',
    bgColor: '#FFFBEB',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    borderColor: '#93C5FD',
    bgColor: '#EFF6FF',
  },
  {
    key: 'ineligible',
    label: 'Ineligible',
    borderColor: '#FECACA',
    bgColor: '#FEF2F2',
  },
];

const NEEDS_REVIEW_REASON_LABELS: Record<string, string> = {
  pending_uniforms: 'Pending uniform review',
  pending_certificates: 'Pending certificate review',
  ai_assessment_for_review: 'AI assessment needs review',
  expired: 'Skill has expired',
  expiring_soon: 'Skill expiring within 30 days',
};

// Reasons that require actual admin review (not just expiration)
const REVIEWABLE_REASONS = new Set([
  'pending_uniforms',
  'pending_certificates',
  'ai_assessment_for_review',
  'pending_quiz',
]);

const hasReviewableReason = (skill: ConsolidatedSkill) =>
  skill.needs_review_reasons?.some((r) => REVIEWABLE_REASONS.has(r));

const SkillChip = ({
  skill,
  section,
  onClick,
}: {
  skill: ConsolidatedSkill;
  section: SectionConfig['key'];
  onClick: () => void;
}) => {
  const jobsCount = skill.worker_skill?.jobs_count ?? 0;
  const progress = skill.requirements_progress;

  const jobsBadge = jobsCount > 0 && (
    <span
      style={{
        backgroundColor: '#E5E7EB',
        color: '#374151',
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 10,
        padding: '1px 6px',
        lineHeight: '16px',
      }}
    >
      {jobsCount}
    </span>
  );

  const getChipContent = () => {
    switch (section) {
      case 'approved': {
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <span>{skill.skill_name}</span>
            {jobsBadge}
          </Stack>
        );
      }
      case 'needs_review': {
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <Warning sx={{ fontSize: 14, color: '#F59E0B' }} />
            <span>{skill.skill_name}</span>
            {jobsBadge}
            {progress && (
              <Small color="lighter">
                {progress.completed}/{progress.total}
              </Small>
            )}
          </Stack>
        );
      }
      case 'in_progress': {
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <span>{skill.skill_name}</span>
            {jobsBadge}
            {progress && (
              <Small color="lighter">
                {progress.completed}/{progress.total}
              </Small>
            )}
          </Stack>
        );
      }
      case 'ineligible':
        return (
          <Stack gap={6} style={{ alignItems: 'center' }}>
            <ErrorIcon sx={{ fontSize: 14, color: '#EF4444' }} />
            <span>{skill.skill_name}</span>
            {jobsBadge}
          </Stack>
        );
    }
  };

  const borderColorMap: Record<string, string> = {
    approved: '#A7F3D0',
    needs_review: '#FDE68A',
    in_progress: '#93C5FD',
    ineligible: '#FECACA',
  };

  const tooltipTitle =
    section === 'needs_review' && skill.needs_review_reasons?.length > 0
      ? skill.needs_review_reasons
          .map((r) => NEEDS_REVIEW_REASON_LABELS[r] || r)
          .join('\n')
      : '';

  const chip = (
    <Chip
      label={getChipContent()}
      onClick={onClick}
      variant="outlined"
      sx={{
        borderColor: borderColorMap[section],
        borderRadius: '8px',
        height: 'auto',
        py: 0.5,
        px: 0.5,
        cursor: 'pointer',
        '& .MuiChip-label': {
          padding: '4px 8px',
        },
        '&:hover': {
          backgroundColor: '#F3F4F6',
        },
      }}
    />
  );

  if (tooltipTitle) {
    return (
      <Tooltip
        title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>}
        arrow
        placement="top"
      >
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

const WorkerSkillsCard = ({ workerId }: Props) => {
  const navigate = useNavigate();
  const [data, setData] = useState<ConsolidatedSkillsResponse['data'] | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await WorkersV2API.getConsolidatedSkills(workerId);
      if (response.success) {
        const grouped = { ...response.data.grouped };
        // Move needs_review skills that only have expiration reasons:
        // - complete skills (all requirements done) → approved
        // - incomplete skills → in_progress
        const actualNeedsReview =
          grouped.needs_review.filter(hasReviewableReason);
        const expirationOnly = grouped.needs_review.filter(
          (s) => !hasReviewableReason(s),
        );
        const isComplete = (s: ConsolidatedSkill) =>
          s.requirements_progress &&
          s.requirements_progress.completed >= s.requirements_progress.total;
        grouped.needs_review = actualNeedsReview;
        grouped.approved = [
          ...grouped.approved,
          ...expirationOnly.filter(isComplete),
        ];
        grouped.in_progress = [
          ...grouped.in_progress,
          ...expirationOnly.filter((s) => !isComplete(s)),
        ];
        setData({ ...response.data, grouped });
      } else {
        setError('Failed to fetch worker skills');
      }
    } catch (err) {
      console.error('Error fetching consolidated skills:', err);
      setError('Failed to fetch worker skills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, [workerId]);

  const handleViewSkill = (skillId: number) => {
    navigate(`/workers_v2/${workerId}/skill/${skillId}`);
  };

  const handleSkillAdded = (skillId: number) => {
    setShowAddModal(false);
    navigate(`/workers_v2/${workerId}/skill/${skillId}`);
  };

  const totalCount = data?.skills?.length ?? 0;

  if (loading) {
    return (
      <Card title="Skills">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">Loading...</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Skills">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">{error}</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={`Skills (${totalCount})`}
        actionsDropdown={
          <Stack gap={8} style={{ alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={(e) => setHelpAnchor(e.currentTarget)}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setShowAddModal(true)}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                minWidth: '140px',
                whiteSpace: 'nowrap',
              }}
            >
              Add Skill
            </Button>
          </Stack>
        }
      >
        {totalCount === 0 ? (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">No skills found</Small>
            </Stack>
          </Card.Section>
        ) : (
          <>
            {SECTIONS.map((section) => {
              const skills = data?.grouped?.[section.key] ?? [];
              if (skills.length === 0) return null;

              return (
                <Card.Section key={section.key}>
                  <Stack vertical gap={12}>
                    <Stack gap={8} style={{ alignItems: 'center' }}>
                      <Body weight="semibold">{section.label}</Body>
                      <Badge
                        label={String(skills.length)}
                        status={
                          section.key === 'approved'
                            ? 'theme'
                            : section.key === 'ineligible'
                              ? 'danger'
                              : section.key === 'needs_review'
                                ? 'warning'
                                : 'neutral'
                        }
                      />
                    </Stack>
                    <Stack
                      gap={8}
                      style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}
                    >
                      {skills.map((skill) => (
                        <SkillChip
                          key={skill.skill_id}
                          skill={skill}
                          section={section.key}
                          onClick={() => handleViewSkill(skill.skill_id)}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Card.Section>
              );
            })}
          </>
        )}
      </Card>

      <Popover
        open={!!helpAnchor}
        anchorEl={helpAnchor}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div style={{ padding: '16px', maxWidth: '360px' }}>
          <Typography variant="subtitle2" gutterBottom>
            Skill Categories
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            <strong>Approved</strong> — Worker has been granted this skill and
            can claim shifts that require it. The number badge shows shifts worked.
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            <strong>Needs Review</strong> — Worker has the skill but something
            needs admin attention (pending certificates, uniforms, AI assessment
            for review, or quiz).
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            <strong>In Progress</strong> — Worker has a skill request and is
            working through the requirements, or has a skill that is expiring
            soon. The fraction shows completed vs total requirements.
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 0 }}>
            <strong>Ineligible</strong> — Worker skill has been disabled by an
            admin. The worker cannot claim shifts for this skill.
          </Typography>
        </div>
      </Popover>

      <AddSkillModal
        open={showAddModal}
        workerId={workerId}
        existingSkillIds={data?.skills?.map((s) => s.skill_id) || []}
        onClose={() => setShowAddModal(false)}
        onSkillAdded={handleSkillAdded}
      />
    </>
  );
};

export default WorkerSkillsCard;
